import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface ActiveUser {
  id: string;
  email: string | null;
  visitorId?: string;
  ip: string;
  device: string;
  browser: string;
  currentPage: string;
  currentActivity: string;
  lastSeen: number;
  loginTime?: number;
  firstSeen?: number;
  isOnline: boolean;
  isAnonymous: boolean;
  isBot: boolean;
  type: 'user' | 'visitor';
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface Stats {
  totalOnline: number;
  loggedInOnline: number;
  visitorsOnline: number;
  botsOnline: number;
  totalLoggedIn: number;
  totalVisitors: number;
  totalBots: number;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8eaed;
  overflow: hidden;
  margin-top: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid #e8eaed;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 22px;
    height: 22px;
    color: white;
  }
`;

const HeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 2px 0 0 0;
`;

const OnlineCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 20px;
  color: white;
  font-size: 13px;
  font-weight: 600;
`;

const OnlineDot = styled.div`
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const UserList = styled.div`
  max-height: 800px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

const UserRow = styled.div<{ $isOnline: boolean }>`
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s ease;
  opacity: ${props => props.$isOnline ? 1 : 0.6};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8fafc;
  }
`;

const UserAvatar = styled.div<{ $isOnline: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$isOnline 
    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
    : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  position: relative;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
`;

const StatusIndicator = styled.div<{ $isOnline: boolean }>`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$isOnline ? '#10b981' : '#94a3b8'};
  border: 2px solid white;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserEmail = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserActivity = styled.div`
  font-size: 12px;
  color: #10b981;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const DeviceBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #f1f5f9;
  border-radius: 6px;
  font-size: 11px;
  color: #475569;
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const IpBadge = styled.div`
  font-size: 10px;
  color: #94a3b8;
  font-family: 'SF Mono', 'Monaco', monospace;
`;

const LogoutButton = styled.button`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  color: white;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
  
  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisitorBadge = styled.span`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  margin-left: 6px;
`;

const BotBadge = styled.span`
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  margin-left: 6px;
`;

const InactiveBadge = styled.span`
  background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 500;
  margin-left: 6px;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
`;

const PaginationButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)' : '#ffffff'};
  color: ${props => props.$active ? 'white' : '#64748b'};
  border: 1px solid ${props => props.$active ? 'transparent' : '#e2e8f0'};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.$active ? 'linear-gradient(135deg, #3b5bdb 0%, #2b4acb 100%)' : '#f1f5f9'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: #64748b;
  padding: 0 8px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  padding: 8px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e8eaed;
`;

const StatBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${props => props.$color}15;
  border-radius: 6px;
  font-size: 11px;
  color: ${props => props.$color};
  font-weight: 500;
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  
  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
  
  p {
    font-size: 14px;
    margin: 0;
  }
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #64748b;
  font-size: 14px;
`;

// Device icon component
const DeviceIcon: React.FC<{ device: string }> = ({ device }) => {
  if (device === 'Mobile') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  if (device === 'Tablet') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  // Desktop
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
};

const VisitorsCard: React.FC = () => {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loggingOut, setLoggingOut] = useState<string | null>(null);
  const [, setTick] = useState(0); // For forcing re-render to update inactive times
  
  // Update inactive times every second
  useEffect(() => {
    const tickInterval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(tickInterval);
  }, []);

  const fetchActiveUsers = useCallback(async (page: number = currentPage) => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/active-users`, {
        params: { page, limit: 10 },
        withCredentials: true
      });

      if (response.data.success) {
        setUsers(response.data.activeUsers);
        setPagination(response.data.pagination);
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching active users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchActiveUsers(currentPage);
    
    // Poll every 5 seconds
    const interval = setInterval(() => fetchActiveUsers(currentPage), 5000);
    
    return () => clearInterval(interval);
  }, [fetchActiveUsers, currentPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchActiveUsers(newPage);
  };

  const handleForceLogout = async (user: ActiveUser) => {
    if (!window.confirm(`Are you sure you want to ${user.isAnonymous ? 'remove' : 'force logout'} ${user.email || user.visitorId}?`)) {
      return;
    }
    
    setLoggingOut(user.id);
    try {
      const response = await axios.post(`${API_BASE}/api/admin/force-logout`, {
        email: user.isAnonymous ? undefined : user.email,
        visitorId: user.isAnonymous ? user.visitorId : undefined
      }, {
        withCredentials: true
      });
      
      if (response.data.success) {
        // Refresh the list
        fetchActiveUsers(currentPage);
      }
    } catch (error) {
      console.error('Error forcing logout:', error);
      alert('Failed to logout user. Please try again.');
    } finally {
      setLoggingOut(null);
    }
  };

  const onlineCount = stats?.totalOnline || users.filter(u => u.isOnline).length;

  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const formatInactiveTime = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 30) return null; // Not inactive yet
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <HeaderLeft>
          <HeaderIcon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </HeaderIcon>
          <div>
            <HeaderTitle>Live Visitors</HeaderTitle>
            <HeaderSubtitle>Real-time user activity</HeaderSubtitle>
          </div>
        </HeaderLeft>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {pagination && pagination.totalPages > 1 && (
            <PaginationInfo>
              Page {pagination.page}/{pagination.totalPages}
            </PaginationInfo>
          )}
          <OnlineCount>
            <OnlineDot />
            {onlineCount} Online
          </OnlineCount>
        </div>
      </CardHeader>

      {/* Stats Row */}
      {stats && (
        <StatsRow>
          <StatBadge $color="#10b981">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            {stats.loggedInOnline} Logged In
          </StatBadge>
          <StatBadge $color="#f59e0b">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
            {stats.visitorsOnline} Visitors
          </StatBadge>
          <StatBadge $color="#8b5cf6">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="10" rx="2" />
              <circle cx="12" cy="5" r="2" />
              <path d="M12 7v4" />
              <circle cx="8" cy="16" r="1" />
              <circle cx="16" cy="16" r="1" />
            </svg>
            {stats.totalBots} Bots
          </StatBadge>
          <StatBadge $color="#64748b">
            Total: {stats.totalLoggedIn + stats.totalVisitors + stats.totalBots}
          </StatBadge>
        </StatsRow>
      )}

      {loading ? (
        <LoadingState>Loading visitors...</LoadingState>
      ) : users.length === 0 ? (
        <EmptyState>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <line x1="23" y1="11" x2="17" y2="11" />
          </svg>
          <p>No active visitors at the moment</p>
        </EmptyState>
      ) : (
        <>
          <UserList>
            {users.map((user) => (
              <UserRow key={user.id} $isOnline={user.isOnline}>
                <UserAvatar $isOnline={user.isOnline}>
                  {user.isBot ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="10" rx="2" />
                      <circle cx="12" cy="5" r="2" />
                      <path d="M12 7v4" />
                      <circle cx="8" cy="16" r="1" />
                      <circle cx="16" cy="16" r="1" />
                    </svg>
                  ) : user.isAnonymous ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  )}
                  <StatusIndicator $isOnline={user.isOnline} />
                </UserAvatar>
                
                <UserInfo>
                  <UserEmail title={user.email || user.visitorId}>
                    {user.email || (user.isBot ? 'Bot/Crawler' : 'Anonymous Visitor')}
                    {user.isBot && <BotBadge>BOT</BotBadge>}
                    {user.isAnonymous && !user.isBot && <VisitorBadge>VISITOR</VisitorBadge>}
                    {formatInactiveTime(user.lastSeen) && (
                      <InactiveBadge>Inactive {formatInactiveTime(user.lastSeen)}</InactiveBadge>
                    )}
                  </UserEmail>
                  <UserActivity>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {user.currentPage} • {user.currentActivity}
                    <span style={{ color: '#94a3b8', marginLeft: '4px' }}>
                      ({formatTimeAgo(user.lastSeen)})
                    </span>
                  </UserActivity>
                </UserInfo>
                
                <UserMeta>
                  <DeviceBadge>
                    <DeviceIcon device={user.device} />
                    {user.device} • {user.browser}
                  </DeviceBadge>
                  <IpBadge>{user.ip}</IpBadge>
                  {user.isOnline && (
                    <LogoutButton 
                      onClick={() => handleForceLogout(user)}
                      disabled={loggingOut === user.id}
                    >
                      {loggingOut === user.id ? '...' : user.isAnonymous ? 'Remove' : 'Logout'}
                    </LogoutButton>
                  )}
                </UserMeta>
              </UserRow>
            ))}
          </UserList>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton 
                onClick={() => handlePageChange(1)}
                disabled={!pagination.hasPrev}
              >
                ««
              </PaginationButton>
              <PaginationButton 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrev}
              >
                «
              </PaginationButton>
              
              <PaginationInfo>
                {pagination.page} of {pagination.totalPages} ({pagination.totalCount} total)
              </PaginationInfo>
              
              <PaginationButton 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNext}
              >
                »
              </PaginationButton>
              <PaginationButton 
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={!pagination.hasNext}
              >
                »»
              </PaginationButton>
            </PaginationContainer>
          )}
        </>
      )}
    </Card>
  );
};

export default VisitorsCard;
